// src/components/SidebarMenu/sidebarNavigation.js
import {
  HomeIcon,
  ClipboardListIcon,
  CubeIcon,
  TruckIcon,
  TagIcon,
  RefreshIcon,
  LocationMarkerIcon,
  CurrencyDollarIcon,
  InboxInIcon,
  ScaleIcon,
  ClipboardCheckIcon,
  UserGroupIcon,
  ScissorsIcon,
  CalendarIcon,
  QrcodeIcon,
  ExclamationIcon,
  PrinterIcon,
  DocumentTextIcon,
  VideoCameraIcon,
} from '@heroicons/react/outline'

import {
  ROOT,
  ORDERS,
  ORDERS_PROCESSING,
  PICKING_TASKS_TO_BREAK,
  FULFILLMENT_ERRORS,
  ORDERS_SHIPPED,
  ROMANEIOS,
  PROOF_VIDEO_COVERAGE,
  PROOF_VIDEO_COVERAGE_PERMISSION,
  GENERATE_LABELS,
  RETURNS,
  LOCATIONS,
  REPLENISHMENTS,
  INBOUND_APPOINTMENTS,
  C2C_RECEIVING,
  PENDING_PRINTS,
  CLAIMS,
  WORK_ORDERS,
  BILLING,
  NATURA_REPLENISHMENTS,
  WORKFORCE,
  CONFIGURATIONS_OPERATORS,
  CONFIGURATIONS_CAIXAS,
  CONFIGURATIONS_STORE_BOXES,
  CONFIGURATIONS_CONTAINER_TEMPLATES,
  CONFIGURATIONS_BARCODE_POOL,
  CONFIGURATIONS_SALES_CHANNELS,
  CONFIGURATIONS_CAMPAIGNS,
  CONFIGURATIONS_RECEIVING,
  CONFIGURATIONS_PACKING,
  CONFIGURATIONS_DISPATCH_RECIPIENTS,
  LASTMILE_SHIPPING_METHODS,
  DISPATCH_SCHEDULES,
  AUTOMATED_COVERAGE_DEACTIVATIONS,
  NATURA_OPS_CREATE_ORDER,
  NATURA_OPS_PICKING,
  NATURA_OPS_NOTA_FISCAL,
  NATURA_OPS_DEPOSIT_TRANSFER,
  NATURA_OPS_NOTFIS_UPLOAD,
  NATURA_OPS_CONFIRMATION_RESEND,
} from '../../navigation/constants'
import { isAdminTierUser, isSuperAdminUser, isWarehouseAdminUser } from '../../utils/userUtils'
import { hasAnyWorkforcePermission } from '../../utils/workforceUtils'
import { userHasPermission } from '../../navigation/NavegationRoutes'

const isAdmin = user => isSuperAdminUser(user) || isWarehouseAdminUser(user)

export const navigation = [
  { id: 1, name: 'dashboard', href: ROOT, permission_key: 'read_order_stats', validate: true, icon: HomeIcon },
  { id: 2, name: 'pending', href: ORDERS, permission_key: 'index_pending_orders', validate: true, icon: ClipboardListIcon },
  { id: 3, name: 'pick_and_pack', href: ORDERS_PROCESSING, permission_key: 'index_processing_orders', validate: true, icon: CubeIcon },
  {
    id: 17,
    name: 'boxes_to_break',
    href: PICKING_TASKS_TO_BREAK,
    permission_key: 'resolve_container_breaks',
    validate: true,
    icon: ScissorsIcon,
    isVisible: user => !!user?.permissions?.beta_features,
  },
  // Orders that dropped out of the packing flow and are waiting on a prerequisite. It goes here, next to
  // Pick & Pack, because that is where the operator left them when moving on to the next order.
  { id: 21, name: 'fulfillment_errors', href: FULFILLMENT_ERRORS, permission_key: 'index_processing_orders', validate: true, icon: ExclamationIcon },
  { id: 4, name: 'shipments', href: ORDERS_SHIPPED, permission_key: 'index_shipped_orders', validate: true, icon: TruckIcon },
  // The carrier handover documents the shipments left in, right after them.
  { id: 23, name: 'romaneios', href: ROMANEIOS, permission_key: 'index_shipping_dispatches', validate: true, icon: DocumentTextIcon },
  { id: 24, name: 'proof_video_coverage', href: PROOF_VIDEO_COVERAGE, permission_key: PROOF_VIDEO_COVERAGE_PERMISSION, validate: true, icon: VideoCameraIcon },
  { id: 5, name: 'generate_labels', href: GENERATE_LABELS, permission_key: 'generate_labels_only', validate: false, icon: TagIcon },
  { id: 6, name: 'returns', href: RETURNS, permission_key: 'create_and_recieve_returns', validate: true, icon: RefreshIcon },
  { id: 20, name: 'cubbitos', href: C2C_RECEIVING, permission_key: 'c2c_receiving_read', validate: true, icon: QrcodeIcon },
  { id: 7, name: 'inventory', href: LOCATIONS, permission_key: 'index_inventory', validate: true, icon: LocationMarkerIcon },
  { id: 8, name: 'load', href: REPLENISHMENTS, permission_key: 'index_replenishments', validate: true, icon: InboxInIcon },
  { id: 19, name: 'inbound_appointments', href: INBOUND_APPOINTMENTS, permission_key: 'complete_replenishment', validate: true, icon: CalendarIcon },
  { id: 9, name: 'claims', href: CLAIMS, permission_key: 'index_claims', validate: true, icon: ScaleIcon },
  { id: 10, name: 'maquilas', href: WORK_ORDERS, permission_key: 'index_work_orders', validate: true, icon: ClipboardCheckIcon },
  { id: 0, name: 'billing', href: BILLING, permission_key: 'create_billing_configuration', validate: true, icon: CurrencyDollarIcon },
  { id: 12, name: 'natura_replenishments', href: NATURA_REPLENISHMENTS, permission_key: 'natura_inbound_management', validate: true, icon: InboxInIcon },
  { id: 16, name: 'workforce', href: WORKFORCE, permission_key: null, validate: true, icon: UserGroupIcon, isVisible: hasAnyWorkforcePermission },
  { id: 22, name: 'pending_prints', href: PENDING_PRINTS, permission_key: null, validate: true, icon: PrinterIcon },
]

export const naturaOpsSubItems = [
  { id: 'natura-ops-create-order', name: 'navegation_routes.natura_ops_create_order', href: NATURA_OPS_CREATE_ORDER, permission_key: 'natura_manaus_poa_ops' },
  { id: 'natura-ops-picking', name: 'navegation_routes.natura_ops_picking', href: NATURA_OPS_PICKING, permission_key: 'natura_manaus_poa_ops' },
  { id: 'natura-ops-nota-fiscal', name: 'navegation_routes.natura_ops_nota_fiscal', href: NATURA_OPS_NOTA_FISCAL, permission_key: 'natura_manaus_poa_ops' },
  {
    id: 'natura-ops-deposit-transfer',
    name: 'navegation_routes.natura_ops_deposit_transfer',
    href: NATURA_OPS_DEPOSIT_TRANSFER,
    permission_key: 'natura_manaus_poa_ops',
  },
  {
    id: 'natura-ops-confirmation-resend',
    name: 'navegation_routes.natura_ops_confirmation_resend',
    href: NATURA_OPS_CONFIRMATION_RESEND,
    permission_key: 'natura_manaus_poa_ops',
  },
  {
    id: 'natura-ops-notfis-upload',
    name: 'navegation_routes.natura_ops_notfis_upload',
    href: NATURA_OPS_NOTFIS_UPLOAD,
    permission_key: 'natura_manaus_poa_ops',
  },
]

export const configurationSubItems = [
  { id: 'config-operators', name: 'configurations.manage_operators', href: CONFIGURATIONS_OPERATORS, canView: isAdmin },
  { id: 'config-boxes', name: 'configurations.manage_boxes', href: CONFIGURATIONS_CAIXAS, permission_key: 'show_shipping_packages' },
  { id: 'config-store-boxes', name: 'configurations.manage_store_boxes', href: CONFIGURATIONS_STORE_BOXES, permission_key: 'show_shipping_packages' },
  {
    id: 'config-container-templates',
    name: 'configurations.manage_container_templates',
    href: CONFIGURATIONS_CONTAINER_TEMPLATES,
    permission_key: 'beta_features',
  },
  { id: 'config-barcode-pool', name: 'configurations.manage_barcode_pool', href: CONFIGURATIONS_BARCODE_POOL, permission_key: 'beta_features' },
  { id: 'config-sales-channels', name: 'configurations.manage_sales_channels', href: CONFIGURATIONS_SALES_CHANNELS, permission_key: 'beta_features' },
  { id: 'config-campaigns', name: 'configurations.campaigns', href: CONFIGURATIONS_CAMPAIGNS, canView: isAdminTierUser },
  { id: 'config-receiving', name: 'configurations.receiving', href: CONFIGURATIONS_RECEIVING, permission_key: 'complete_replenishment' },
  { id: 'config-packing', name: 'configurations.packing', href: CONFIGURATIONS_PACKING, permission_key: 'manage_packing_audit_configurations' },
  { id: 'config-shipping-methods', name: 'lastmile_shipping_methods', href: LASTMILE_SHIPPING_METHODS, permission_key: 'index_shipping_methods_management' },
  { id: 'config-dispatch', name: 'dispatch_schedules', href: DISPATCH_SCHEDULES, permission_key: 'index_dispatch_schedules' },
  // Same people as the dispatch schedules: they decide who receives each carrier's dispatch document.
  {
    id: 'config-dispatch-recipients',
    name: 'configurations.dispatch_recipients',
    href: CONFIGURATIONS_DISPATCH_RECIPIENTS,
    permission_key: 'index_dispatch_schedules',
  },
  {
    id: 'config-coverage-deactivations',
    name: 'automated_coverage_deactivations',
    href: AUTOMATED_COVERAGE_DEACTIVATIONS,
    permission_key: 'manage_automated_shipping_coverage_deactivations',
  },
]

const getConfigurationsSection = path => path?.match(/\/configurations\/([^/]+)/)?.[1] || null

const subItemAllows = (item, user) => {
  // beta_features is opt-in: admins do not bypass it.
  if (item.permission_key === 'beta_features') {
    return userHasPermission(user, 'beta_features')
  }
  return isAdmin(user) || (item.canView ? item.canView(user) : userHasPermission(user, item.permission_key))
}

// Per-warehouse visibility hook for sidebar items whose feature is opt-in at
// the warehouse level. Currently no sidebar items use this gate, but the hook
// is kept so future warehouse-flagged items can plug in without touching the
// SidebarMenu component.
// eslint-disable-next-line no-unused-vars
export const isSidebarItemVisibleForWarehouse = (item, currentWarehouse) => true

// Used by PrivateRoute to gate /configurations and /configurations/* paths
// without requiring an admin role — each sub-item enforces its own rule.
// Admins (super or warehouse) keep blanket access to preserve existing behavior.
export const canAccessConfigurationsPath = (user, path) => {
  if (!user) return false
  const section = getConfigurationsSection(path)

  // Bare /configurations path (redirects to a sub-route): allow if any
  // sub-item under /configurations/* is accessible.
  if (!section) {
    return configurationSubItems.some(item => getConfigurationsSection(item.href) && subItemAllows(item, user))
  }

  const matched = configurationSubItems.find(item => getConfigurationsSection(item.href) === section)
  return matched ? subItemAllows(matched, user) : false
}
